import { IonIcon, IonText } from '@ionic/react';
import { Formik } from 'formik';
import { paperPlaneOutline } from 'ionicons/icons';
import { ReactElement, useRef, useState } from 'react';
import * as yup from 'yup';
import { useCreateScratchpadRecommendationFeedbackMutation } from '../../../../../../../api/graphql/generated';
import { CenterAlignedButtons } from '../../../../../../../components/button/aligned-buttons';
import { LoadingChipButton } from '../../../../../../../components/button/loading-chip-button';
import { SaveStatusToast } from '../../../../../../../components/toast/toast.gql';
import { Collapsible } from '../../../../../../../components/ui/collapsible';
import { getLocalizedText } from '../../../../../../../i18n/i18n.utils';
import { sleep } from '../../../../../../../util/async';
import { RatingInput } from '../../../../../shared/component/widget/rating-input';
import { TextInput } from '../../../../../shared/component/widget/text-input';
import './recommendation-feedback.css';
import { SingleWeldRecommendation } from './settings-recommendation.hook';

interface RecommendationFeedbackProps {
  recommendation?: SingleWeldRecommendation;
  afterSubmit: () => void;
}

interface RecommendationFeedbackForm {
  scratchpadRecommendationId: string;
  rating: number;
  comments?: string;
}

const ScratchpadRecommendationFeedback = (
  props: RecommendationFeedbackProps
): ReactElement => {
  const { recommendation, afterSubmit } = props;

  const feedbackValidation = yup.object().shape({
    rating: yup.number().integer().min(1).max(5).required().default(undefined),
    comments: yup.string(),
  });

  const [createScratchpadRecommendationFeedback, { loading, error }] =
    useCreateScratchpadRecommendationFeedbackMutation();

  const [showFeedback, setShowFeedback] = useState(false);
  const feedbackDivRef = useRef<HTMLDivElement>(null);

  const onSubmit = async (form: RecommendationFeedbackForm) => {
    const { rating, ...rest } = form;
    const createFeedbackResult = await createScratchpadRecommendationFeedback({
      variables: {
        input: {
          rating: Number(rating),
          ...rest,
        },
      },
    });

    if (createFeedbackResult.errors?.length ?? 0 === 0) {
      setShowFeedback(false);
      afterSubmit();
    }
  };

  return (
    <>
      {recommendation && (
        <>
          <Formik
            initialValues={{
              scratchpadRecommendationId: recommendation.id,
              rating: 0,
              comments: '',
            }}
            onSubmit={onSubmit}
            enableReinitialize={true}
            validationSchema={feedbackValidation}
          >
            {({ submitForm, isValid, dirty }) => {
              const disabled = !dirty || !isValid;

              return (
                <div>
                  <div className="configuration-header">
                    <p className="ion-text-right">
                      <IonText
                        className="recommendations-feedback-toggle"
                        color="primary"
                        onClick={() => setShowFeedback(!showFeedback)}
                      >
                        {getLocalizedText(
                          'common:share_recommendation_feedback'
                        )}
                      </IonText>
                    </p>
                  </div>
                  <Collapsible
                    collapsed={!showFeedback}
                    onExpand={async () => {
                      // this sleep is necessary to ensure the div is at full height before scrolling
                      await sleep(250);
                      // we need to scroll into view since the div is rendered, but below the visible area
                      feedbackDivRef.current?.scrollIntoView({
                        behavior: 'smooth',
                      });
                    }}
                  >
                    <div ref={feedbackDivRef} className="feedback-container">
                      <RatingInput
                        name="rating"
                        label={getLocalizedText('common:rating')}
                        description={getLocalizedText(
                          'common:rating_description'
                        )}
                        showDescription={true}
                      />
                      <TextInput
                        label={getLocalizedText('common:comments')}
                        name="comments"
                        ionItemProps={{ lines: 'none' }}
                      />
                      <CenterAlignedButtons>
                        <LoadingChipButton
                          loading={loading}
                          chipProps={{
                            color: 'primary',
                            onClick: submitForm,
                            disabled,
                          }}
                        >
                          {getLocalizedText('common:submit_feedback')}
                          <IonIcon icon={paperPlaneOutline} />
                        </LoadingChipButton>
                      </CenterAlignedButtons>
                    </div>
                  </Collapsible>
                </div>
              );
            }}
          </Formik>
        </>
      )}
      <SaveStatusToast
        loading={loading}
        error={error}
        defaultErrorMessageKey="common:recommendation_feedback_error"
      />
    </>
  );
};

export { ScratchpadRecommendationFeedback };
